import type { CheckBookableRequest } from './schema/CheckBookableRequest';
import type { CheckBookableResponse } from './schema/CheckBookableResponse';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { isAnonymousUser } from 'bookings-shared/lib/c2/utils/isAnonymousUser';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getAccessTokenforResource } from 'owa-tokenprovider';

import { setGraphTokenState } from '../actions/setGraphTokenState';
import getBookingMailboxGuid from '../selectors/getBookingMailboxGuid';
import getBookingMailboxPuid from '../selectors/getBookingMailboxPuid';
import getBookingMeetingTypeId from '../selectors/getBookingMeetingTypeId';
import getCustomerInfoTenantName from '../selectors/getCustomerInfoTenantName';
import getEntryPoint from '../selectors/getEntryPoint';
import { GraphTokenState } from '../store/schema/GraphToken';
import store from '../store/store';
import { validatedFetchForSignedUrl } from '../utils/validatedFetch';

export async function checkBookableAndUpdateTotalBookings(
    shouldBook: boolean,
    requestUrl?: string
): Promise<CheckBookableResponse | null> {
    const perfDatapoint = new PerformanceDatapoint('checkBookableAndUpdateTotalBookings');
    const requestBody: CheckBookableRequest = {
        shouldBook,
    };

    var headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const request: RequestInit = {
        method: 'PATCH',
        headers,
        body: JSON.stringify(requestBody),
    };

    // when the flight is on we can allow going into the if statement
    // this code is currently flighted as when getAccessTokenforResource is called and the browser used is firefox
    // getAccessTokenforResource is known to fail upon multiple refresh
    // TODO: clean up unused code and contact CoreAuth team regarding status of firebox repro
    // we are not currently using this code to validate if specific users have access to invite only links
    if (!isAnonymousUser() && isFeatureEnabled('bookwithme-AdditionalAuthorizationGraphToken')) {
        /* eslint-disable-next-line owa-custom-rules/require-mailboxInfoParameter  -- (https://aka.ms/OWALintWiki)
         * MailboxInfo must be provided to the function, see https://aka.ms/multiaccountlinter
         *	> MailboxInfo must be passed into the function to identify the account. */
        const graphToken = await getAccessTokenforResource(
            'https://graph.microsoft.com',
            'checkBookableAndUpdateTotalBookingsPatch'
        );
        if (graphToken) {
            headers.append('Additional-Authorization', graphToken);
            setGraphTokenState(GraphTokenState.Successful);
        } else {
            setGraphTokenState(GraphTokenState.Failed);
        }
    }

    try {
        const bookingSignedUrl = requestUrl ? requestUrl : store.bookingSignedUrl;
        if (bookingSignedUrl) {
            const response = await validatedFetchForSignedUrl(bookingSignedUrl, request);
            if (response.ok) {
                const data = (await response.json()) as CheckBookableResponse;
                perfDatapoint.addCustomData({
                    meetingTypeId: getBookingMeetingTypeId(),
                    entryPoint: getEntryPoint(),
                    shouldBook,
                    tenantName: getCustomerInfoTenantName(),
                    bookingMailboxGuid: getBookingMailboxGuid(),
                    loggedInUserHashedEmail: store.bookingData?.LoggedInUserHashedEmail ?? '',
                    userClientId: store.bookingData?.UserClientId ?? '',
                    bookingMailboxPuid: getBookingMailboxPuid(),
                });
                perfDatapoint.end();
                return data;
            }
        }
        perfDatapoint.addCustomData({
            hasSignedUrl: !!bookingSignedUrl,
            hasRequestUrl: !!requestUrl,
            shouldBook,
            meetingTypeId: getBookingMeetingTypeId(),
            entryPoint: getEntryPoint(),
            tenantName: getCustomerInfoTenantName(),
            bookingMailboxGuid: getBookingMailboxGuid(),
            loggedInUserHashedEmail: store.bookingData?.LoggedInUserHashedEmail ?? '',
            userClientId: store.bookingData?.UserClientId ?? '',
            bookingMailboxPuid: getBookingMailboxPuid(),
        });
        // exclusively for the client error
        if (!bookingSignedUrl) {
            perfDatapoint.endWithError(DatapointStatus.ClientError);
        } else {
            // this is in case the response
            // coming from the server is not ok
            perfDatapoint.endWithError(DatapointStatus.ServerError);
        }
    } catch (ex) {
        perfDatapoint.addCustomData({
            hasRequestUrl: !!requestUrl,
            shouldBook,
            meetingTypeId: getBookingMeetingTypeId(),
            entryPoint: getEntryPoint(),
            tenantName: getCustomerInfoTenantName(),
            exception: JSON.stringify(ex),
            bookingMailboxGuid: getBookingMailboxGuid(),
            loggedInUserHashedEmail: store.bookingData?.LoggedInUserHashedEmail ?? '',
            userClientId: store.bookingData?.UserClientId ?? '',
            bookingMailboxPuid: getBookingMailboxPuid(),
        });
        perfDatapoint.endWithError(DatapointStatus.ServerError, ex);
    }

    return null;
}
